import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponseWithData } from "../models/system";
import { IInvoice } from "../models/invoice/IInvoice";
import { useAuth } from "../contexts/AuthContext";
import { resourceAPIURL } from "../config";
import { IInvoiceItem, IEnsureSelfBilledInvoiceForCollectionRequest } from "../models/Invoice";
import { fileNameFromContentDisposition } from "../utils/contentDisposition";

const invoiceFilters = (filters?: IInvoiceFilters) => {
  const params: Record<string, string> = {};
  if (filters?.status) params.status = filters.status;
  if (filters?.accountId) params.accountId = filters.accountId;
  if (filters?.partId) params.partId = filters.partId;
  if (filters?.from) params.from = filters.from;
  if (filters?.to) params.to = filters.to;
  return params;
};

interface IInvoiceFilters {
  status?: string;
  accountId?: string;
  partId?: string;
  from?: string;
  to?: string;
}

export default function useInvoices(filters?: IInvoiceFilters, enabled: boolean = true) {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();
  const { claims, token } = useAuth();

  // GET: All Invoices
  const invoices = useQuery({
    queryKey: ["invoices"],
    queryFn: () => get<DefaultResponseWithData<IInvoice[]>>("/invoice"),
    enabled: claims?.isAdmin && enabled,
  });

  const filteredInvoices = useQuery({
    queryKey: ["invoices", filters],
    queryFn: () => get<DefaultResponseWithData<IInvoice[]>>("/invoice/filtered?" + new URLSearchParams(invoiceFilters(filters)).toString()),
    enabled: claims?.isAdmin && enabled,
  });

  // GET: Single Invoice
  // const getInvoice = (invoiceId: string) =>
  //   useQuery({
  //     queryKey: ["invoices", invoiceId],
  //     queryFn: () =>
  //       get<DefaultResponseWithData<IInvoice>>(`/invoice/${invoiceId}`),
  //     enabled: !!invoiceId && claims?.isAdmin,
  //   });

  const createItem = useMutation({
    mutationFn: (payload: any) =>
      post<DefaultResponseWithData<IInvoice>>(`/invoice/${payload.invoiceId}/item`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
    },
  });

  // POST: Create Invoice
  const createInvoice = useMutation({
    mutationFn: (payload: any) =>
      post<DefaultResponseWithData<IInvoice>>("/invoice", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
  });

  /** Idempotent self-billed purchase invoice for a vehicle collection (optionally scoped to a lead). */
  const ensureSelfBilledInvoiceForCollection = useMutation({
    mutationFn: (body: IEnsureSelfBilledInvoiceForCollectionRequest) =>
      post<IEnsureSelfBilledInvoiceForCollectionRequest, DefaultResponseWithData<IInvoice>>(
        "/invoice/self-billed/collection",
        body
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["lead"] });
    },
  });

  // PUT: Update Invoice
  const updateInvoice = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      put<DefaultResponseWithData<IInvoice>>(`/invoice/${id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
    },
  });

  const updateInvoiceItem = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      put<DefaultResponseWithData<IInvoiceItem>>(`/invoice/${id}/item/${payload.id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
    }
  });

  // DELETE: Delete Invoice
  const deleteInvoice = useMutation({
    mutationFn: (id: string) =>
      del<DefaultResponseWithData<boolean>>(`/invoice/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
  });

  const downloadInvoice = async (invoiceId: string) => {
    try {
      const response = await fetch(`${resourceAPIURL}/api/invoice/${invoiceId}/download`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          Authorization: `Bearer ${token}`
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download invoice.");
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const fileName = fileNameFromContentDisposition(
        contentDisposition,
        `invoice_${invoiceId}.pdf`
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download error:", error);
      // Optional: Show toast or UI error
    }
  };

  const downloadBulkInvoices = async (invoiceIds: string[], service: string) => {
    try {
      const response = await fetch(`${resourceAPIURL}/api/invoice/export/${service}/bulk`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ids: invoiceIds })
      });

      if (!response.ok) {
        throw new Error("Failed to download invoices.");
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const currentTimestamp = new Date().toISOString().replace(/[:.-]/g, "");
      const fileName = fileNameFromContentDisposition(
        contentDisposition,
        `invoices_${currentTimestamp}.csv`
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download error:", error);
      // Optional: Show toast or UI error
    }
  }

  return {
    invoices,
    filteredInvoices,
    // getInvoice,
    createInvoice,
    ensureSelfBilledInvoiceForCollection,
    updateInvoice,
    deleteInvoice,
    downloadInvoice,
    downloadBulkInvoices,

    createItem,

    updateInvoiceItem
  };
}
